import {
  WSAlert,
  WSElement,
  WSList,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import React, { useMemo, useState } from "react";
import {
  IPayerResponse,
  PayerPayeeStatus,
  RequirementStatus
} from "@wingspanhq/payments/dist/interfaces";
import {
  RequirementContextType,
  RequirementTableItemData
} from "../../../Requirements/types";
import { useUserId } from "../../../../query/hooks/helpers";
import { useHistory } from "react-router-dom";
import { ExtendedRequirementsTable } from "../../../Requirements/components/ExtendedRequirementsTable";
import { ExtendedRequirementPreviewList } from "../../../Requirements/components/ExtendedRequirementPreviewList";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { ExtendedRequirementItem } from "../../../Requirements/modules/GeneralRequirements/types";
import { usePayerExtendedGeneralRequirements } from "../../../Requirements/hooks/usePayerExtendedGeneralRequirements";

export type EligibilityPanelProps = {
  payer: IPayerResponse;
  noActions?: boolean;
  hasEligibleEngagements: boolean;
  requirements: IRequirementResponse[];
};

export const EligibilityRequirements: React.FC<EligibilityPanelProps> = ({
  hasEligibleEngagements,
  requirements,
  payer,
  noActions
}) => {
  const history = useHistory();
  const userId = useUserId();

  const generalRequirements = usePayerExtendedGeneralRequirements(payer);

  const allRequirements = useMemo(() => {
    return [...generalRequirements.requirements, ...requirements];
  }, [generalRequirements.requirements, requirements]);

  const completedLength = useMemo(() => {
    return allRequirements.filter(e => e.status === RequirementStatus.Complete)
      .length;
  }, [allRequirements]);

  const isRequirementsComplete = useMemo(() => {
    return allRequirements.every(e => e.status === RequirementStatus.Complete);
  }, [allRequirements]);

  const [showPreviews, setShowPreviews] = useState(false);

  const getOnViewDetails = (requirement: ExtendedRequirementItem) => {
    return "requirementId" in requirement
      ? (requirement: IRequirementResponse) =>
          history.push(
            `/member/payers/${payer.payerId}/requirement/${requirement.requirementId}`
          )
      : undefined;
  };

  return (
    <WSElement>
      <WSList gap="L">
        <WSSectionToolbar
          title="Requirements"
          pill={
            payer.payeeOwnedData.status === PayerPayeeStatus.Inactive
              ? {
                  icon: "archive",
                  text: "Archived",
                  theme: "neutral"
                }
              : {
                  icon: true,
                  text: `${completedLength} of ${allRequirements.length} completed`,
                  theme:
                    isRequirementsComplete && hasEligibleEngagements
                      ? "success"
                      : "warning"
                }
          }
          buttons={[
            {
              label: "",
              onClick: () => setShowPreviews(true),
              kind: showPreviews ? "Secondary" : "Link",
              icon: "grid",
              onlyIcon: true,
              size: "S"
            },
            {
              label: "",
              onClick: () => setShowPreviews(false),
              kind: !showPreviews ? "Secondary" : "Link",
              icon: "list",
              onlyIcon: true,
              size: "S"
            }
          ]}
        />
        {showPreviews ? (
          <ExtendedRequirementPreviewList
            requirements={allRequirements}
            payeeId={userId}
            payerId={payer.payerId}
            contextType={RequirementContextType.Payer}
            getOnViewDetails={getOnViewDetails}
          />
        ) : (
          <ExtendedRequirementsTable
            payeeId={userId}
            payerId={payer.payerId}
            requirements={allRequirements}
            contextType={RequirementContextType.Payer}
            showOverrideActions
            hideActions={noActions}
            getOnViewDetails={getOnViewDetails}
          />
        )}
      </WSList>
    </WSElement>
  );
};
